import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Card,
  CardContent,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  FormControlLabel,
  Chip,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Divider,
} from '@mui/material';
import StraightenIcon from "@mui/icons-material/Straighten";
import ResidentAvatar from '../components/ResidentAvatar';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { usePatientDiagnosis } from '../hooks/usePatientDiagnosis';
import residentService from '../services/residentService';
import { ResidentDTO } from '../types/resident.types';
import { formatToBackendDate } from '../utils/dateUtils';
import masterDataService from '../services/masterDataService';
import PatientDiagnosisSvgIcon from '../components/icons/PatientDiagnosisSvgIcon';
import BloodPressureSvgIcon from '../components/icons/BloodPressureSvgIcon';
import WeightSvgIcon from '../components/icons/WeightSvgIcon';
import BmiSvgIcon from '../components/icons/BmiSvgIcon';
import BloodGroupSvgIcon from '../components/icons/BloodGroupSvgIcon';
import patientDiagnosisService from '../services/patientDiagnosisService';
import vitalsService from '../services/vitalsService';
import { useAuth } from '../contexts/AuthContext';


// Validation schema (basic for now, can be extended later)
const diagnosisSchema = yup.object({
  dateOfExam: yup.date().required("Date of Exam is required").typeError("Valid Date of Exam is required"),

  height: yup.string(),
  weight: yup.string(),
  bloodPressure: yup.string(),
  tbTestGivenDate: yup.date().nullable(),
  tbTestReadDate: yup.date().nullable(),
  tbTestType: yup.string(),
  tbTestResult: yup.string(),
  tbResultsMm: yup.string(),
  tbActionTaken: yup.string(),
  chestXrayResults: yup.string(),
  tbStatus: yup.string(),
  primaryDiagnosis: yup.string(),
  primaryCanManage: yup.string(),
  primarySupervisionNeeded: yup.string(),
  secondaryDiagnosis: yup.string(),
  secondaryCanManage: yup.string(),
  secondarySupervisionNeeded: yup.string(),
  mildCognitiveImpairment: yup.boolean(),
  dementia: yup.boolean(),
  contagiousTreatment: yup.string(),
  contagiousCanManage: yup.string(),
  contagiousSupervisionNeeded: yup.string(),

  // Allergies and other conditions
  allergiesTreatment: yup.string(),
  allergiesCanManage: yup.string(),
  allergiesSupervisionNeeded: yup.string(),
  otherConditionsTreatment: yup.string(),
  otherConditionsCanManage: yup.string(),
  otherConditionsSupervisionNeeded: yup.string(),

  // Physical Health Status --- individual flags + assistive device + explanation fields
  auditoryImpairment: yup.boolean(),
  auditoryAssistiveDevice: yup.string(),
  auditoryExplain: yup.string(),

  visualImpairment: yup.boolean(),
  visualAssistiveDevice: yup.string(),
  visualExplain: yup.string(),

  wearsDentures: yup.boolean(),
  wearsDenturesAssistiveDevice: yup.string(),
  wearsDenturesExplain: yup.string(),

  wearsProsthesis: yup.boolean(),
  wearsProsthesisAssistiveDevice: yup.string(),
  wearsProsthesisExplain: yup.string(),

  specialDiet: yup.boolean(),
  specialDietExplain: yup.string(),

  substanceAbuseProblem: yup.boolean(),
  useOfAlcohol: yup.boolean(),
  useOfCigarettes: yup.boolean(),

  bowelImpairment: yup.boolean(),
  bladderImpairment: yup.boolean(),
  motorImpairment: yup.boolean(),
  requiresContinuousBedCare: yup.boolean(),
  historyOfSkinCondition: yup.boolean(),

  // Mental Condition fields (Yes/No plus explain)
  mentalConfused: yup.string(),
  mentalConfusedExplain: yup.string(),
  mentalInappropriate: yup.string(),
  mentalInappropriateExplain: yup.string(),
  mentalAggressive: yup.string(),
  mentalAggressiveExplain: yup.string(),
  mentalWandering: yup.string(),
  mentalWanderingExplain: yup.string(),
  mentalSundowning: yup.string(),
  mentalSundowningExplain: yup.string(),
  mentalAbleFollowInstructions: yup.string(),
  mentalAbleFollowInstructionsExplain: yup.string(),
  mentalDepressed: yup.string(),
  mentalDepressedExplain: yup.string(),
  mentalSuicidal: yup.string(),
  mentalSuicidalExplain: yup.string(),
  mentalAbleCommunicateNeeds: yup.string(),
  mentalAbleCommunicateNeedsExplain: yup.string(),
  mentalAtRiskGrooming: yup.string(),
  mentalAtRiskGroomingExplain: yup.string(),
  mentalAbleLeaveUnassisted: yup.string(),
  mentalAbleLeaveUnassistedExplain: yup.string(),

  // Capacity for self-care
  capBath: yup.string(),
  capBathExplain: yup.string(),
  capDressGroom: yup.string(),
  capDressGroomExplain: yup.string(),
  capFeed: yup.string(),
  capFeedExplain: yup.string(),
  capToileting: yup.string(),
  capToiletingExplain: yup.string(),
  capManageCash: yup.string(),
  capManageCashExplain: yup.string(),

  // Medication management
  medAdminPrescription: yup.string(),
  medAdminPrescriptionExplain: yup.string(),
  medAdminInjections: yup.string(),
  medAdminInjectionsExplain: yup.string(),
  medGlucoseTesting: yup.string(),
  medGlucoseTestingExplain: yup.string(),
  medAdminPRN: yup.string(),
  medAdminPRNExplain: yup.string(),
  medAdminOxygen: yup.string(),
  medAdminOxygenExplain: yup.string(),
  medStoreMedications: yup.string(),
  medStoreMedicationsExplain: yup.string(),

  // Ambulatory / Bedridden fields (new)
  ambulatoryStatus: yup.string(),
  ambulatoryStatusBasedOn: yup.string().nullable(),
  bedriddenIllness: yup.string(),
  bedriddenRecoveryFromSurgery: yup.string(),
  bedriddenOther: yup.string(),
  bedriddenDurationDays: yup.string().matches(/^[0-9]*$/, "Must be a number"),
  bedriddenEstimatedDate: yup.date().nullable(),
  bedriddenPermanentExplain: yup.string(),

  // Hospice / Physical Health / Physician fields (new)
  receivingHospiceCare: yup.string(),
  hospiceTerminalIllness: yup.string(),
  physicalHealthStatus: yup.string(),
  comments: yup.string(),
  physicianNameAddress: yup.string(),
  physicianTelephone: yup.string(),
  physicianLengthOfTimePatient: yup.string(),
  // physicianSignature: yup.string().required("Physician signature is required"),
  physicianDate: yup.date().nullable(),
});

type FormData = yup.InferType<typeof diagnosisSchema>;

// Mock residents for patient selection
const mockResidents = [
  {
    id: 1, name: 'John Smith', dob: '1954-03-15', sex: 'Male', allergies: ['Penicillin'], attendingPhysician: 'Dr. Johnson', room: '201', bp: '120/80', weight: '58Kg', height: '5ft 6in', bmi: '22', EMRNo: 'A12345'
  },
  { id: 2, name: 'Mary Johnson', dob: '1948-07-22', sex: 'Female', allergies: ['Latex', 'Peanuts'], attendingPhysician: 'Dr. Williams', room: '202', bp: '130/85', weight: '62Kg', height: '5ft 4in', bmi: '24', EMRNo: 'A12346' },
  { id: 3, name: 'Robert Williams', dob: '1965-11-08', sex: 'Male', allergies: [], attendingPhysician: 'Dr. Brown', room: '203', bp: '125/82', weight: '75Kg', height: '6ft 0in', bmi: '23', EMRNo: 'A12347' },
  { id: 4, name: 'Jennifer Brown', dob: '1972-05-30', sex: 'Female', allergies: ['Sulfa'], attendingPhysician: 'Dr. Davis', room: '204', bp: '118/78', weight: '55Kg', height: '5ft 5in', bmi: '21', EMRNo: 'A12348' },
];

const PatientDiagnosisPage = (): JSX.Element => {
  const { user } = useAuth();

  const normalizedRole = (user?.role ?? '')
    .toString()
    .toUpperCase()
    .replace(/^ROLE_/, '')
    .trim();

  // Restricted roles: NURSE, CAREGIVER, and LEAD_CAREGIVER all have read-only access.
  // Only SUPER_ADMIN, ADMIN, and FACILITY_ADMIN may create/edit diagnoses.
  const isCaregiver =
    normalizedRole === 'NURSE' ||
    normalizedRole === 'CAREGIVER' ||
    normalizedRole === 'LEAD_CAREGIVER';

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [success, setSuccess] = useState(false);
  // Start on first tab (Exam & Vitals) by default
  const [activeStep, setActiveStep] = useState(0);
  const [selectedResident, setSelectedResident] = useState<string | null>(null); // Changed to string for API
  const [patientDetails, setPatientDetails] = useState<any | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tbResultOptions, setTbResultOptions] = useState<{ id: string; valueName: string }[]>([]);
  const [tbStatusOptions, setTbStatusOptions] = useState<{ id: string; valueName: string }[]>([]);
  const [nonambulatoryOptions, setNonambulatoryOptions] = useState<{ id: string; valueName: string }[]>([]);
  const [ambulatoryStatusOptions, setAmbulatoryStatusOptions] = useState<{ id: string; valueName: string }[]>([]);
  const [treatmentStatusOptions, setTreatmentStatusOptions] = useState<{ id: string; valueName: string }[]>([]);
  const [physicalHealthStatusOptions, setPhysicalHealthStatusOptions] = useState<{ id: string; valueName: string }[]>([]);
  const [residentBloodGroup, setResidentBloodGroup] = useState<string | null>(null);
  const [latestVitalBp, setLatestVitalBp] = useState<string | null>(null);
  const [latestVitalWeight, setLatestVitalWeight] = useState<string | null>(null);

  // Support loading an existing diagnosis directly via:
  // - navigation state: { diagnosisId }
  // - query param: ?diagnosisId=<uuid>
  const diagnosisIdFromState = (location.state as any)?.diagnosisId as string | undefined;
  const diagnosisIdFromQuery = searchParams.get('diagnosisId') || undefined;
  const diagnosisId = diagnosisIdFromState || diagnosisIdFromQuery;

  // Fetch TB Test Result dynamic lookups (Step 0 - Exam & Vitals)
  useEffect(() => {
    if (activeStep !== 0 || tbResultOptions.length > 0) return;
    const fetchTbOptions = async () => {
      try {
        const data = await masterDataService.getActiveLookupValuesByName('TBTestResult');
        setTbResultOptions(data || []);
      } catch (err) {
        console.error("Failed to fetch TB Test Result lookups", err);
        // Fallback to standard defaults if API fails
        setTbResultOptions([
          { id: 'neg', valueName: 'Negative' },
          { id: 'pos', valueName: 'Positive' }
        ]);
      }
    };
    fetchTbOptions();
  }, [activeStep, tbResultOptions.length]);

  useEffect(() => {
    if (activeStep !== 0 || tbStatusOptions.length > 0) return;
    const fetchTbStatusOptions = async () => {
      try {
        const data = await masterDataService.getActiveLookupValuesByName('TBStatus');
        setTbStatusOptions(data || []);
      } catch (err) {
        console.error("Failed to fetch TB Status lookups", err);
        // Fallback to standard defaults if API fails
        setTbStatusOptions([
          { id: 'neg', valueName: 'Latent TB Infection' },
          { id: 'pos', valueName: 'Active TB Disease' },
          { id: 'neg', valueName: 'No Evidence of TB Infection or Disease' },
        ]);
      }
    };
    fetchTbStatusOptions();
  }, [activeStep, tbStatusOptions.length]);

  // Fetch Ambulatory Status lookups (Step 1 - Ambulatory)
  useEffect(() => {
    if (activeStep !== 1 || nonambulatoryOptions.length > 0) return;
    const fetchNonambulatoryOptions = async () => {
      try {
        const data = await masterDataService.getActiveLookupValuesByName('NonambulatoryBedridden');
        setNonambulatoryOptions(data || []);
      } catch (err) {
        console.error("Failed to fetch Nonambulatory Status lookups", err);
        setNonambulatoryOptions([
          { id: 'Physical Condition', valueName: 'Physical Condition' },
          { id: 'Mental Condition', valueName: 'Mental Condition' },
          { id: 'Both Physical and Mental Condition', valueName: 'Both Physical and Mental Condition' },
        ]);
      }
    };
    fetchNonambulatoryOptions();
  }, [activeStep, nonambulatoryOptions.length]);

  useEffect(() => {
    if (activeStep !== 1 || ambulatoryStatusOptions.length > 0) return;
    const fetchAmbulatoryStatusOptions = async () => {
      try {
        const data = await masterDataService.getActiveLookupValuesByName('AmbulatoryStatus');
        setAmbulatoryStatusOptions(data || []);
      } catch (err) {
        console.error("Failed to fetch Ambulatory Status lookups", err);
        setAmbulatoryStatusOptions([
          { id: 'Ambulatory', valueName: 'Ambulatory' },
          { id: 'Nonambulatory', valueName: 'Nonambulatory' },
          { id: 'Bedridden', valueName: 'Bedridden' },
        ]);
      }
    };
    fetchAmbulatoryStatusOptions();
  }, [activeStep, ambulatoryStatusOptions.length]);

  // Fetch Treatment Status lookups (Step 2 & Step 6)
  useEffect(() => {
    if ((activeStep !== 2 && activeStep !== 6) || treatmentStatusOptions.length > 0) return;
    const fetchTreatmentStatusOptions = async () => {
      try {
        const data = await masterDataService.getActiveLookupValuesByName('TreatmentStatus');
        setTreatmentStatusOptions(data || []);
      } catch (err) {
        console.error("Failed to fetch Treatment Status lookups", err);
        setTreatmentStatusOptions([
          { id: 'Yes', valueName: 'Yes' },
          { id: 'No', valueName: 'No' },
        ]);
      }
    };
    fetchTreatmentStatusOptions();
  }, [activeStep, treatmentStatusOptions.length]);

  // Fetch Physical Health Status lookups (Step 6)
  useEffect(() => {
    if (activeStep !== 6 || physicalHealthStatusOptions.length > 0) return;
    const fetchPhysicalHealthStatusOptions = async () => {
      try {
        const data = await masterDataService.getActiveLookupValuesByName('PhysicalHealthStatus');
        setPhysicalHealthStatusOptions(data || []);
      } catch (err) {
        console.error("Failed to fetch Physical Health Status lookups", err);
        setPhysicalHealthStatusOptions([
          { id: 'Good', valueName: 'Good' },
          { id: 'Fair', valueName: 'Fair' },
          { id: 'Poor', valueName: 'Poor' },
        ]);
      }
    };
    fetchPhysicalHealthStatusOptions();
  }, [activeStep, physicalHealthStatusOptions.length]);

  // Initialize the patient diagnosis hook
  const {
    diagnosis,
    loading,
    saving,
    error,
    updateExamVitals,
    updateAmbulatoryStatus,
    updateDiagnosesTreatments,
    updatePhysicalHealthAssessment,
    updateMentalConditionAssessment,
    updateCapacityMedicationAssessment,
    updateHospicePhysicianInfo,
    submitDiagnosis,
    isEditable,
    completionPercentage,
  } = usePatientDiagnosis({ residentId: selectedResident, diagnosisId });

  const isReadOnlyForm = !isEditable || isCaregiver;

  // ──────────────────────────────────────────────────────────────────────────

  // If the diagnosis load provided the resident id, hydrate the resident selection for dependent UI.
  useEffect(() => {
    if (diagnosis?.residentId && !selectedResident) {
      setSelectedResident(String(diagnosis.residentId));
    }
  }, [diagnosis, selectedResident]);

  // Fetch full resident record to get bloodGroup (same pattern as ClinicalNotesPage)
  useEffect(() => {
    const residentId = selectedResident || (diagnosis?.residentId ? String(diagnosis.residentId) : null);
    if (!residentId) return;
    residentService.getResidentById(residentId)
      .then((res: any) => {
        if (res?.bloodGroup) setResidentBloodGroup(res.bloodGroup);
      })
      .catch(() => {/* silently ignore — blood group will fall back to N/A */ });

    vitalsService.getVitals(residentId)
      .then((data: any) => {
        let history = Array.isArray(data) ? data : data ? [data] : [];
        history = history.filter((v) => String(v.residentId) === String(residentId));
        history.sort((a, b) => new Date(b.vitalDate || 0).getTime() - new Date(a.vitalDate || 0).getTime());
        if (history.length > 0) {
          const latestBpVital = history.find((v: any) => v.dailyBloodPressure || v.bloodPressure);
          if (latestBpVital) {
            setLatestVitalBp(latestBpVital.dailyBloodPressure || latestBpVital.bloodPressure);
          }
          const latestWeightVital = history.find((v: any) => v.dailyWeight || v.weight);
          if (latestWeightVital) {
            setLatestVitalWeight(latestWeightVital.dailyWeight || latestWeightVital.weight);
          }
        }
      })
      .catch(() => { });
  }, [selectedResident, diagnosis?.residentId]);

  const saveDisabled: boolean = !!(
    Boolean(saving) ||
    !isEditable ||
    isCaregiver ||
    (diagnosis && (String(diagnosis.status).toUpperCase() === "REVIEWED") && Number(completionPercentage) === 100)
  );

  const {
    control,
    handleSubmit,
    trigger,
    formState: { errors },
    reset,
    getValues,
  } = useForm<FormData>({
    mode: 'onChange',
    resolver: yupResolver(diagnosisSchema),
    defaultValues: {
      sex: '',
      tbTestResult: 'Negative',
      tbStatus: 'No Evidence of TB Infection or Disease',
      primaryCanManage: 'No',
      secondaryCanManage: 'No',
      contagiousCanManage: 'No',
      mildCognitiveImpairment: false,
      dementia: false,
      // defaults for new fields
      allergiesCanManage: 'No',
      otherConditionsCanManage: 'No',
      auditoryImpairment: false,
      visualImpairment: false,
      wearsDentures: false,
      wearsProsthesis: false,
      specialDiet: false,
      substanceAbuseProblem: false,
      useOfAlcohol: false,
      useOfCigarettes: false,
      bowelImpairment: false,
      bladderImpairment: false,
      motorImpairment: false,
      requiresContinuousBedCare: false,
      historyOfSkinCondition: false,
      // mental condition defaults
      mentalConfused: 'No',
      mentalInappropriate: 'No',
      mentalAggressive: 'No',
      mentalWandering: 'No',
      mentalSundowning: 'No',
      mentalAbleFollowInstructions: 'No',
      mentalDepressed: 'No',
      mentalSuicidal: 'No',
      mentalAbleCommunicateNeeds: 'No',
      mentalAtRiskGrooming: 'No',
      mentalAbleLeaveUnassisted: 'No',
      // capacity defaults
      capBath: 'No',
      capDressGroom: 'No',
      capFeed: 'No',
      capToileting: 'No',
      capManageCash: 'No',
      // medication management defaults
      medAdminPrescription: 'No',
      medAdminInjections: 'No',
      medGlucoseTesting: 'No',
      medAdminPRN: 'No',
      medAdminOxygen: 'No',
      medStoreMedications: 'No',

      // ambulatory defaults (new)
      ambulatoryStatus: 'Ambulatory',
      ambulatoryStatusBasedOn: '',
      bedriddenIllness: '',
      bedriddenRecoveryFromSurgery: '',
      bedriddenOther: '',
      bedriddenDurationDays: '',
      bedriddenEstimatedDate: null,
      bedriddenPermanentExplain: '',

      // hospice / physical health / physician defaults (new)
      receivingHospiceCare: '',
      hospiceTerminalIllness: '',
      physicalHealthStatus: '',
      comments: '',
      physicianNameAddress: '',
      physicianTelephone: '',
      physicianLengthOfTimePatient: '',
      physicianSignature: '',
      physicianDate: null,
    } as any,
  });

  const steps = [
    'Exam & Vitals',
    'Ambulatory',
    'Diagnosis & Treatments',
    'Physical Health',
    'Mental Condition',
    'Capacity & Medication',
    'Hospice & Physician',
  ];

  // Populate form when diagnosis loads from API
  useEffect(() => {
    if (isSubmitting) return;

    if (diagnosis) {
      // Start with current values relative to defaults to ensure nothing is undefined/dropped
      const formData: any = {
        sex: '',
        tbTestResult: 'Negative',
        tbStatus: 'No Evidence of TB Infection or Disease',
        primaryCanManage: 'No',
        secondaryCanManage: 'No',
        contagiousCanManage: 'No',
        mildCognitiveImpairment: false,
        dementia: false,
        allergiesCanManage: 'No',
        otherConditionsCanManage: 'No',
        auditoryImpairment: false,
        visualImpairment: false,
        wearsDentures: false,
        wearsProsthesis: false,
        specialDiet: false,
        substanceAbuseProblem: false,
        useOfAlcohol: false,
        useOfCigarettes: false,
        bowelImpairment: false,
        bladderImpairment: false,
        motorImpairment: false,
        requiresContinuousBedCare: false,
        historyOfSkinCondition: false,
        mentalConfused: 'No',
        mentalInappropriate: 'No',
        mentalAggressive: 'No',
        mentalWandering: 'No',
        mentalSundowning: 'No',
        mentalAbleFollowInstructions: 'No',
        mentalDepressed: 'No',
        mentalSuicidal: 'No',
        mentalAbleCommunicateNeeds: 'No',
        mentalAtRiskGrooming: 'No',
        mentalAbleLeaveUnassisted: 'No',
        capBath: 'No',
        capDressGroom: 'No',
        capFeed: 'No',
        capToileting: 'No',
        capManageCash: 'No',
        medAdminPrescription: 'No',
        medAdminInjections: 'No',
        medGlucoseTesting: 'No',
        medAdminPRN: 'No',
        medAdminOxygen: 'No',
        medStoreMedications: 'No',
        ambulatoryStatus: 'Ambulatory',
        ambulatoryStatusBasedOn: '',
        bedriddenIllness: '',
        bedriddenRecoveryFromSurgery: '',
        bedriddenOther: '',
        bedriddenDurationDays: '',
        bedriddenEstimatedDate: null,
        bedriddenPermanentExplain: '',
        receivingHospiceCare: '',
        hospiceTerminalIllness: '',
        physicalHealthStatus: '',
        comments: '',
        physicianNameAddress: '',
        physicianTelephone: '',
        physicianLengthOfTimePatient: '',
        physicianSignature: '',
        physicianDate: null,
      };

      // Exam & Vitals (Tab 1)
      if (diagnosis.examVitals) {
        // Backend may provide exam/vitals either under examVitals.* or at the diagnosis root.
        // Prefer examVitals.* but fall back to root fields when missing.
        formData.dateOfExam = diagnosis.examVitals.dateOfExam
          ? new Date(diagnosis.examVitals.dateOfExam)
          : diagnosis.diagnosisDate
            ? new Date(diagnosis.diagnosisDate)
            : null;
        const diagnosisAny = diagnosis as any;
        formData.height = diagnosis.examVitals.height || diagnosisAny.height || '';
        formData.weight = diagnosis.examVitals.weight || diagnosisAny.weight || '';
        formData.bloodPressure = diagnosis.examVitals.bloodPressure || diagnosisAny.bp || '';
        formData.tbTestGivenDate = diagnosis.examVitals.tbTestGivenDate ? new Date(diagnosis.examVitals.tbTestGivenDate) : null;
        formData.tbTestReadDate = diagnosis.examVitals.tbTestReadDate ? new Date(diagnosis.examVitals.tbTestReadDate) : null;
        formData.tbTestType = diagnosis.examVitals.tbTestType || '';
        formData.tbTestResult = diagnosis.examVitals.tbTestResult || 'Negative';
        formData.tbResultsMm = diagnosis.examVitals.tbResultsMm || '';
        formData.tbActionTaken = diagnosis.examVitals.tbActionTaken || '';
        formData.chestXrayResults = diagnosis.examVitals.chestXrayResults || '';

        // Normalize TB status values coming from API so they match the dropdown option labels.
        const normalizeTbStatus = (val: any) => {
          if (val === null || val === undefined || String(val).trim() === '') {
            return 'No Evidence of TB Infection or Disease';
          }
          const s = String(val).trim();
          if (/active/i.test(s)) return 'Active TB Disease';
          if (/latent/i.test(s)) return 'Latent TB Infection';
          // handle variations like 'no_evidence', 'no evidence of tb', 'none', etc.
          if (/no[_\s-]*evidence|noevidence|none|no.*tb/i.test(s)) return 'No Evidence of TB Infection or Disease';
          // If it's already one of the exact labels, return as-is; otherwise return the raw string.
          return s;
        };

        formData.tbStatus = normalizeTbStatus(diagnosis.examVitals.tbStatus);
      }

      // Ambulatory Status (Tab 2)
      if (diagnosis.ambulatoryStatus) {
        formData.ambulatoryStatus = diagnosis.ambulatoryStatus.ambulatoryStatus || 'Ambulatory';
        formData.ambulatoryStatusBasedOn = diagnosis.ambulatoryStatus.statusBasedOn || '';
        formData.bedriddenIllness = diagnosis.ambulatoryStatus.bedriddenIllness || '';
        formData.bedriddenRecoveryFromSurgery = diagnosis.ambulatoryStatus.bedriddenRecoveryFromSurgery || '';
        formData.bedriddenOther = diagnosis.ambulatoryStatus.bedriddenOther || '';
        formData.bedriddenDurationDays = diagnosis.ambulatoryStatus.bedriddenDurationDays?.toString() || '';
        formData.bedriddenEstimatedDate = diagnosis.ambulatoryStatus.bedriddenEstimatedEndDate ? new Date(diagnosis.ambulatoryStatus.bedriddenEstimatedEndDate) : null;
        formData.bedriddenPermanentExplain = diagnosis.ambulatoryStatus.bedriddenPermanentExplain || '';
      }

      // Diagnosis & Treatments (Tab 3)
      if (diagnosis.diagnosesTreatments) {
        formData.primaryDiagnosis = diagnosis.diagnosesTreatments.primaryDiagnosis || '';
        formData.primaryCanManage = diagnosis.diagnosesTreatments.primaryCanManage || 'No';
        formData.primarySupervisionNeeded = diagnosis.diagnosesTreatments.primarySupervisionNeeded || '';
        formData.secondaryDiagnosis = diagnosis.diagnosesTreatments.secondaryDiagnosis || '';
        formData.secondaryCanManage = diagnosis.diagnosesTreatments.secondaryCanManage || 'No';
        formData.secondarySupervisionNeeded = diagnosis.diagnosesTreatments.secondarySupervisionNeeded || '';
        formData.mildCognitiveImpairment = diagnosis.diagnosesTreatments.mildCognitiveImpairment || false;
        formData.dementia = diagnosis.diagnosesTreatments.dementia || false;
        formData.contagiousTreatment = diagnosis.diagnosesTreatments.contagiousTreatment || '';
        formData.contagiousCanManage = diagnosis.diagnosesTreatments.contagiousCanManage || 'No';
        formData.contagiousSupervisionNeeded = diagnosis.diagnosesTreatments.contagiousSupervisionNeeded || '';
        formData.allergiesTreatment = diagnosis.diagnosesTreatments.allergiesTreatment || '';
        formData.allergiesCanManage = diagnosis.diagnosesTreatments.allergiesCanManage || 'No';
        formData.allergiesSupervisionNeeded = diagnosis.diagnosesTreatments.allergiesSupervisionNeeded || '';
        formData.otherConditionsTreatment = diagnosis.diagnosesTreatments.otherConditionsTreatment || '';
        formData.otherConditionsCanManage = diagnosis.diagnosesTreatments.otherConditionsCanManage || 'No';
        formData.otherConditionsSupervisionNeeded = diagnosis.diagnosesTreatments.otherConditionsSupervisionNeeded || '';
      }

      // Physical Health (Tab 4)
      if (diagnosis.physicalHealthAssessment?.assessmentData) {
        const data = diagnosis.physicalHealthAssessment.assessmentData;
        formData.auditoryImpairment = data.auditoryImpairment || false;
        formData.auditoryAssistiveDevice = data.auditoryAssistiveDevice || '';
        formData.auditoryExplain = data.auditoryExplain || '';

        formData.visualImpairment = data.visualImpairment || false;
        formData.visualAssistiveDevice = data.visualAssistiveDevice || '';
        formData.visualExplain = data.visualExplain || '';

        formData.wearsDentures = data.wearsDentures || false;
        formData.wearsDenturesAssistiveDevice = data.wearsDenturesAssistiveDevice || '';
        formData.wearsDenturesExplain = data.wearsDenturesExplain || '';

        formData.wearsProsthesis = data.wearsProsthesis || false;
        formData.wearsProsthesisAssistiveDevice = data.wearsProsthesisAssistiveDevice || '';
        formData.wearsProsthesisExplain = data.wearsProsthesisExplain || '';

        formData.specialDiet = data.specialDiet || false;
        formData.specialDietExplain = data.specialDietExplain || '';

        formData.substanceAbuseProblem = data.substanceAbuseProblem || false;
        formData.useOfAlcohol = data.useOfAlcohol || false;
        formData.useOfCigarettes = data.useOfCigarettes || false;

        formData.bowelImpairment = data.bowelImpairment || false;
        formData.bladderImpairment = data.bladderImpairment || false;
        formData.motorImpairment = data.motorImpairment || false;
        formData.requiresContinuousBedCare = data.requiresContinuousBedCare || false;
        formData.historyOfSkinCondition = data.historyOfSkinCondition || false;
      }

      // Mental Condition (Tab 5) - Extract from assessmentData
      if (diagnosis.mentalConditionAssessment?.assessmentData) {
        const data = diagnosis.mentalConditionAssessment.assessmentData;
        formData.mentalConfused = data.mentalConfused || 'No';
        formData.mentalConfusedExplain = data.mentalConfusedExplain || '';
        formData.mentalInappropriate = data.mentalInappropriate || 'No';
        formData.mentalInappropriateExplain = data.mentalInappropriateExplain || '';
        formData.mentalAggressive = data.mentalAggressive || 'No';
        formData.mentalAggressiveExplain = data.mentalAggressiveExplain || '';
        formData.mentalWandering = data.mentalWandering || 'No';
        formData.mentalWanderingExplain = data.mentalWanderingExplain || '';
        formData.mentalSundowning = data.mentalSundowning || 'No';
        formData.mentalSundowningExplain = data.mentalSundowningExplain || '';
        formData.mentalAbleFollowInstructions = data.mentalAbleFollowInstructions || 'No';
        formData.mentalAbleFollowInstructionsExplain = data.mentalAbleFollowInstructionsExplain || '';
        formData.mentalDepressed = data.mentalDepressed || 'No';
        formData.mentalDepressedExplain = data.mentalDepressedExplain || '';
        formData.mentalSuicidal = data.mentalSuicidal || 'No';
        formData.mentalSuicidalExplain = data.mentalSuicidalExplain || '';
        formData.mentalAbleCommunicateNeeds = data.mentalAbleCommunicateNeeds || 'No';
        formData.mentalAbleCommunicateNeedsExplain = data.mentalAbleCommunicateNeedsExplain || '';
        formData.mentalAtRiskGrooming = data.mentalAtRiskGrooming || 'No';
        formData.mentalAtRiskGroomingExplain = data.mentalAtRiskGroomingExplain || '';
        formData.mentalAbleLeaveUnassisted = data.mentalAbleLeaveUnassisted || 'No';
        formData.mentalAbleLeaveUnassistedExplain = data.mentalAbleLeaveUnassistedExplain || '';
      }

      // Capacity & Medication (Tab 6) - Extract from assessmentData
      if (diagnosis.capacityMedicationAssessment?.assessmentData) {
        const data = diagnosis.capacityMedicationAssessment.assessmentData;
        formData.capBath = data.capBath || 'No';
        formData.capBathExplain = data.capBathExplain || '';
        formData.capDressGroom = data.capDressGroom || 'No';
        formData.capDressGroomExplain = data.capDressGroomExplain || '';
        formData.capFeed = data.capFeed || 'No';
        formData.capFeedExplain = data.capFeedExplain || '';
        formData.capToileting = data.capToileting || 'No';
        formData.capToiletingExplain = data.capToiletingExplain || '';
        formData.capManageCash = data.capManageCash || 'No';
        formData.capManageCashExplain = data.capManageCashExplain || '';
        formData.medAdminPrescription = data.medAdminPrescription || 'No';
        formData.medAdminPrescriptionExplain = data.medAdminPrescriptionExplain || '';
        formData.medAdminInjections = data.medAdminInjections || 'No';
        formData.medAdminInjectionsExplain = data.medAdminInjectionsExplain || '';
        formData.medGlucoseTesting = data.medGlucoseTesting || 'No';
        formData.medGlucoseTestingExplain = data.medGlucoseTestingExplain || '';
        formData.medAdminPRN = data.medAdminPRN || 'No';
        formData.medAdminPRNExplain = data.medAdminPRNExplain || '';
        formData.medAdminOxygen = data.medAdminOxygen || 'No';
        formData.medAdminOxygenExplain = data.medAdminOxygenExplain || '';
        formData.medStoreMedications = data.medStoreMedications || 'No';
        formData.medStoreMedicationsExplain = data.medStoreMedicationsExplain || '';
      }

      // Hospice & Physician (Tab 7)
      if (diagnosis.hospicePhysicianInfo) {
        formData.receivingHospiceCare = diagnosis.hospicePhysicianInfo.receivingHospiceCare || '';
        formData.hospiceTerminalIllness = diagnosis.hospicePhysicianInfo.hospiceTerminalIllness || '';
        formData.physicalHealthStatus = diagnosis.hospicePhysicianInfo.physicalHealthStatus || '';
        formData.comments = diagnosis.hospicePhysicianInfo.comments || '';
        formData.physicianNameAddress = diagnosis.hospicePhysicianInfo.physicianNameAddress || '';
        formData.physicianTelephone = diagnosis.hospicePhysicianInfo.physicianTelephone || '';
        formData.physicianLengthOfTimePatient = diagnosis.hospicePhysicianInfo.physicianLengthOfTimePatient || '';
        formData.physicianSignature = diagnosis.hospicePhysicianInfo.physicianSignature || '';
        formData.physicianDate = diagnosis.hospicePhysicianInfo.physicianDate ? new Date(diagnosis.hospicePhysicianInfo.physicianDate) : null;
      }

      reset(formData);
    }
  }, [diagnosis, reset, isSubmitting]);

  // Auto-save current tab before moving forward
  const saveCurrentTab = async (tabIndex: number) => {
    const formData = getValues();

    switch (tabIndex) {
      case 0: // Exam & Vitals
        await updateExamVitals({
          dateOfExam: formatToBackendDate(formData.dateOfExam),
          height: formData.height,
          weight: formData.weight,
          bloodPressure: formData.bloodPressure,
          tbTestGivenDate: formatToBackendDate(formData.tbTestGivenDate),
          tbTestReadDate: formatToBackendDate(formData.tbTestReadDate),
          tbTestType: formData.tbTestType,
          tbTestResult: formData.tbTestResult,
          tbResultsMm: formData.tbResultsMm,
          tbActionTaken: formData.tbActionTaken,
          chestXrayResults: formData.chestXrayResults,
          tbStatus: formData.tbStatus,
        });
        break;

      case 1: // Ambulatory Status
        await updateAmbulatoryStatus({
          ambulatoryStatus: formData.ambulatoryStatus,
          statusBasedOn: formData.ambulatoryStatus === 'Ambulatory' ? null : formData.ambulatoryStatusBasedOn,
          bedriddenIllness: formData.bedriddenIllness,
          bedriddenRecoveryFromSurgery: formData.bedriddenRecoveryFromSurgery,
          bedriddenOther: formData.bedriddenOther,
          bedriddenDurationDays: formData.bedriddenDurationDays ? parseInt(formData.bedriddenDurationDays, 10) : undefined,
          bedriddenEstimatedEndDate: formatToBackendDate(formData.bedriddenEstimatedDate),
          bedriddenPermanentExplain: formData.bedriddenPermanentExplain,
        });
        break;

      case 2: // Diagnosis & Treatments
        await updateDiagnosesTreatments({
          primaryDiagnosis: formData.primaryDiagnosis,
          primaryCanManage: formData.primaryCanManage,
          primarySupervisionNeeded: formData.primarySupervisionNeeded,
          secondaryDiagnosis: formData.secondaryDiagnosis,
          secondaryCanManage: formData.secondaryCanManage,
          secondarySupervisionNeeded: formData.secondarySupervisionNeeded,
          mildCognitiveImpairment: formData.mildCognitiveImpairment,
          dementia: formData.dementia,
          contagiousTreatment: formData.contagiousTreatment,
          contagiousCanManage: formData.contagiousCanManage,
          contagiousSupervisionNeeded: formData.contagiousSupervisionNeeded,
          allergiesTreatment: formData.allergiesTreatment,
          allergiesCanManage: formData.allergiesCanManage,
          allergiesSupervisionNeeded: formData.allergiesSupervisionNeeded,
          otherConditionsTreatment: formData.otherConditionsTreatment,
          otherConditionsCanManage: formData.otherConditionsCanManage,
          otherConditionsSupervisionNeeded: formData.otherConditionsSupervisionNeeded,
        });
        break;

      case 3: // Physical Health Assessment
        await updatePhysicalHealthAssessment({
          assessmentData: {
            auditoryImpairment: formData.auditoryImpairment,
            auditoryAssistiveDevice: formData.auditoryAssistiveDevice,
            auditoryExplain: formData.auditoryExplain,
            visualImpairment: formData.visualImpairment,
            visualAssistiveDevice: formData.visualAssistiveDevice,
            visualExplain: formData.visualExplain,
            wearsDentures: formData.wearsDentures,
            wearsDenturesAssistiveDevice: formData.wearsDenturesAssistiveDevice,
            wearsDenturesExplain: formData.wearsDenturesExplain,
            wearsProsthesis: formData.wearsProsthesis,
            wearsProsthesisAssistiveDevice: formData.wearsProsthesisAssistiveDevice,
            wearsProsthesisExplain: formData.wearsProsthesisExplain,
            specialDiet: formData.specialDiet,
            specialDietExplain: formData.specialDietExplain,
            substanceAbuseProblem: formData.substanceAbuseProblem,
            useOfAlcohol: formData.useOfAlcohol,
            useOfCigarettes: formData.useOfCigarettes,
            bowelImpairment: formData.bowelImpairment,
            bladderImpairment: formData.bladderImpairment,
            motorImpairment: formData.motorImpairment,
            requiresContinuousBedCare: formData.requiresContinuousBedCare,
            historyOfSkinCondition: formData.historyOfSkinCondition,
          },
        });
        break;

      case 4: // Mental Condition Assessment
        await updateMentalConditionAssessment({
          assessmentData: {
            mentalConfused: formData.mentalConfused,
            mentalConfusedExplain: formData.mentalConfusedExplain,
            mentalInappropriate: formData.mentalInappropriate,
            mentalInappropriateExplain: formData.mentalInappropriateExplain,
            mentalAggressive: formData.mentalAggressive,
            mentalAggressiveExplain: formData.mentalAggressiveExplain,
            mentalWandering: formData.mentalWandering,
            mentalWanderingExplain: formData.mentalWanderingExplain,
            mentalSundowning: formData.mentalSundowning,
            mentalSundowningExplain: formData.mentalSundowningExplain,
            mentalAbleFollowInstructions: formData.mentalAbleFollowInstructions,
            mentalAbleFollowInstructionsExplain: formData.mentalAbleFollowInstructionsExplain,
            mentalDepressed: formData.mentalDepressed,
            mentalDepressedExplain: formData.mentalDepressedExplain,
            mentalSuicidal: formData.mentalSuicidal,
            mentalSuicidalExplain: formData.mentalSuicidalExplain,
            mentalAbleCommunicateNeeds: formData.mentalAbleCommunicateNeeds,
            mentalAbleCommunicateNeedsExplain: formData.mentalAbleCommunicateNeedsExplain,
            mentalAtRiskGrooming: formData.mentalAtRiskGrooming,
            mentalAtRiskGroomingExplain: formData.mentalAtRiskGroomingExplain,
            mentalAbleLeaveUnassisted: formData.mentalAbleLeaveUnassisted,
            mentalAbleLeaveUnassistedExplain: formData.mentalAbleLeaveUnassistedExplain,
          },
        });
        break;

      case 5: // Capacity & Medication Assessment
        await updateCapacityMedicationAssessment({
          assessmentData: {
            capBath: formData.capBath,
            capBathExplain: formData.capBathExplain,
            capDressGroom: formData.capDressGroom,
            capDressGroomExplain: formData.capDressGroomExplain,
            capFeed: formData.capFeed,
            capFeedExplain: formData.capFeedExplain,
            capToileting: formData.capToileting,
            capToiletingExplain: formData.capToiletingExplain,
            capManageCash: formData.capManageCash,
            capManageCashExplain: formData.capManageCashExplain,
            medAdminPrescription: formData.medAdminPrescription,
            medAdminPrescriptionExplain: formData.medAdminPrescriptionExplain,
            medAdminInjections: formData.medAdminInjections,
            medAdminInjectionsExplain: formData.medAdminInjectionsExplain,
            medGlucoseTesting: formData.medGlucoseTesting,
            medGlucoseTestingExplain: formData.medGlucoseTestingExplain,
            medAdminPRN: formData.medAdminPRN,
            medAdminPRNExplain: formData.medAdminPRNExplain,
            medAdminOxygen: formData.medAdminOxygen,
            medAdminOxygenExplain: formData.medAdminOxygenExplain,
            medStoreMedications: formData.medStoreMedications,
            medStoreMedicationsExplain: formData.medStoreMedicationsExplain,
          },
        });
        break;

      case 6: // Hospice & Physician Info
        await updateHospicePhysicianInfo({
          receivingHospiceCare: formData.receivingHospiceCare || undefined,
          hospiceTerminalIllness: formData.hospiceTerminalIllness,
          physicalHealthStatus: formData.physicalHealthStatus || undefined,
          comments: formData.comments,
          physicianNameAddress: formData.physicianNameAddress,
          physicianTelephone: formData.physicianTelephone,
          physicianLengthOfTimePatient: formData.physicianLengthOfTimePatient,
          // physicianSignature: formData.physicianSignature,
          physicianDate: formatToBackendDate(formData.physicianDate),
        });
        break;
    }
  };

  const onFinalSubmit = async (data: FormData) => {
    if (isCaregiver) return;
    if (isSubmitting || loading) return;

    setIsSubmitting(true);

    try {



      for (let i = 0; i <= 6; i++) {

        const fields = stepFieldMap[i];
        if (fields && fields.length > 0) {
          const valid = await trigger(fields as any);
          if (!valid) {
            console.error(`[onFinalSubmit] Validation failed for tab ${i}`);
            setSnackbar({
              open: true,
              message: `Please complete the required fields in the "${steps[i]}" section before finishing.`,
              severity: 'error'
            });
            setActiveStep(i);
            setIsSubmitting(false);
            return;
          }
        }
      }

      const payload = {
        residentId: selectedResident as string,
        diagnosisDate: formatToBackendDate(data.dateOfExam) || new Date().toISOString().split('T')[0],
        examVitals: {
          dateOfExam: formatToBackendDate(data.dateOfExam),
          height: data.height,
          weight: data.weight,
          bloodPressure: data.bloodPressure,
          tbTestGivenDate: formatToBackendDate(data.tbTestGivenDate),
          tbTestReadDate: formatToBackendDate(data.tbTestReadDate),
          tbTestType: data.tbTestType,
          tbTestResult: data.tbTestResult,
          tbResultsMm: data.tbResultsMm,
          tbActionTaken: data.tbActionTaken,
          chestXrayResults: data.chestXrayResults,
          tbStatus: data.tbStatus,
        },
        ambulatoryStatus: {
          ambulatoryStatus: data.ambulatoryStatus,
          statusBasedOn: data.ambulatoryStatus === 'Ambulatory' ? null : data.ambulatoryStatusBasedOn,
          bedriddenIllness: data.bedriddenIllness,
          bedriddenRecoveryFromSurgery: data.bedriddenRecoveryFromSurgery,
          bedriddenOther: data.bedriddenOther,
          bedriddenDurationDays: data.bedriddenDurationDays ? parseInt(data.bedriddenDurationDays, 10) : undefined,
          bedriddenEstimatedEndDate: formatToBackendDate(data.bedriddenEstimatedDate),
          bedriddenPermanentExplain: data.bedriddenPermanentExplain,
        },
        diagnosesTreatments: {
          primaryDiagnosis: data.primaryDiagnosis,
          primaryCanManage: data.primaryCanManage,
          primarySupervisionNeeded: data.primarySupervisionNeeded,
          secondaryDiagnosis: data.secondaryDiagnosis,
          secondaryCanManage: data.secondaryCanManage,
          secondarySupervisionNeeded: data.secondarySupervisionNeeded,
          mildCognitiveImpairment: data.mildCognitiveImpairment,
          dementia: data.dementia,
          contagiousTreatment: data.contagiousTreatment,
          contagiousCanManage: data.contagiousCanManage,
          contagiousSupervisionNeeded: data.contagiousSupervisionNeeded,
          allergiesTreatment: data.allergiesTreatment,
          allergiesCanManage: data.allergiesCanManage,
          allergiesSupervisionNeeded: data.allergiesSupervisionNeeded,
          otherConditionsTreatment: data.otherConditionsTreatment,
          otherConditionsCanManage: data.otherConditionsCanManage,
          otherConditionsSupervisionNeeded: data.otherConditionsSupervisionNeeded,
        },
        physicalHealthAssessment: {
          assessmentData: {
            auditoryImpairment: data.auditoryImpairment,
            auditoryAssistiveDevice: data.auditoryAssistiveDevice,
            auditoryExplain: data.auditoryExplain,
            visualImpairment: data.visualImpairment,
            visualAssistiveDevice: data.visualAssistiveDevice,
            visualExplain: data.visualExplain,
            wearsDentures: data.wearsDentures,
            wearsDenturesAssistiveDevice: data.wearsDenturesAssistiveDevice,
            wearsDenturesExplain: data.wearsDenturesExplain,
            wearsProsthesis: data.wearsProsthesis,
            wearsProsthesisAssistiveDevice: data.wearsProsthesisAssistiveDevice,
            wearsProsthesisExplain: data.wearsProsthesisExplain,
            specialDiet: data.specialDiet,
            specialDietExplain: data.specialDietExplain,
            substanceAbuseProblem: data.substanceAbuseProblem,
            useOfAlcohol: data.useOfAlcohol,
            useOfCigarettes: data.useOfCigarettes,
            bowelImpairment: data.bowelImpairment,
            bladderImpairment: data.bladderImpairment,
            motorImpairment: data.motorImpairment,
            requiresContinuousBedCare: data.requiresContinuousBedCare,
            historyOfSkinCondition: data.historyOfSkinCondition,
          },
        },
        mentalConditionAssessment: {
          assessmentData: {
            mentalConfused: data.mentalConfused,
            mentalConfusedExplain: data.mentalConfusedExplain,
            mentalInappropriate: data.mentalInappropriate,
            mentalInappropriateExplain: data.mentalInappropriateExplain,
            mentalAggressive: data.mentalAggressive,
            mentalAggressiveExplain: data.mentalAggressiveExplain,
            mentalWandering: data.mentalWandering,
            mentalWanderingExplain: data.mentalWanderingExplain,
            mentalSundowning: data.mentalSundowning,
            mentalSundowningExplain: data.mentalSundowningExplain,
            mentalAbleFollowInstructions: data.mentalAbleFollowInstructions,
            mentalAbleFollowInstructionsExplain: data.mentalAbleFollowInstructionsExplain,
            mentalDepressed: data.mentalDepressed,
            mentalDepressedExplain: data.mentalDepressedExplain,
            mentalSuicidal: data.mentalSuicidal,
            mentalSuicidalExplain: data.mentalSuicidalExplain,
            mentalAbleCommunicateNeeds: data.mentalAbleCommunicateNeeds,
            mentalAbleCommunicateNeedsExplain: data.mentalAbleCommunicateNeedsExplain,
            mentalAtRiskGrooming: data.mentalAtRiskGrooming,
            mentalAtRiskGroomingExplain: data.mentalAtRiskGroomingExplain,
            mentalAbleLeaveUnassisted: data.mentalAbleLeaveUnassisted,
            mentalAbleLeaveUnassistedExplain: data.mentalAbleLeaveUnassistedExplain,
          },
        },
        capacityMedicationAssessment: {
          assessmentData: {
            capBath: data.capBath,
            capBathExplain: data.capBathExplain,
            capDressGroom: data.capDressGroom,
            capDressGroomExplain: data.capDressGroomExplain,
            capFeed: data.capFeed,
            capFeedExplain: data.capFeedExplain,
            capToileting: data.capToileting,
            capToiletingExplain: data.capToiletingExplain,
            capManageCash: data.capManageCash,
            capManageCashExplain: data.capManageCashExplain,
            medAdminPrescription: data.medAdminPrescription,
            medAdminPrescriptionExplain: data.medAdminPrescriptionExplain,
            medAdminInjections: data.medAdminInjections,
            medAdminInjectionsExplain: data.medAdminInjectionsExplain,
            medGlucoseTesting: data.medGlucoseTesting,
            medGlucoseTestingExplain: data.medGlucoseTestingExplain,
            medAdminPRN: data.medAdminPRN,
            medAdminPRNExplain: data.medAdminPRNExplain,
            medAdminOxygen: data.medAdminOxygen,
            medAdminOxygenExplain: data.medAdminOxygenExplain,
            medStoreMedications: data.medStoreMedications,
            medStoreMedicationsExplain: data.medStoreMedicationsExplain,
          },
        },
        hospicePhysicianInfo: {
          receivingHospiceCare: data.receivingHospiceCare || undefined,
          hospiceTerminalIllness: data.hospiceTerminalIllness,
          physicalHealthStatus: data.physicalHealthStatus || undefined,
          comments: data.comments,
          physicianNameAddress: data.physicianNameAddress,
          physicianTelephone: data.physicianTelephone,
          physicianLengthOfTimePatient: data.physicianLengthOfTimePatient,
          physicianDate: formatToBackendDate(data.physicianDate),
        }
      };
      if (diagnosis?.id) {
        await patientDiagnosisService.updateDiagnosis(diagnosis.id, payload);
        setSnackbar({
          open: true,
          message: 'Diagnosis updated successfully!',
          severity: 'success',
        });
        setSuccess(true);
        setTimeout(() => {
          navigate('/residents');
        }, 1500);
      } else {
        await patientDiagnosisService.createDiagnosis(payload);
        setSnackbar({
          open: true,
          message: 'Diagnosis saved successfully!',
          severity: 'success',
        });
        setSuccess(true);
        setTimeout(() => {
          navigate('/residents');
        }, 1500);
      }
    } catch (error: any) {
      console.error('Error submitting diagnosis:', error);
      setSnackbar({
        open: true,
        message: 'Failed to submit diagnosis: ' + (error.response?.data?.message || error.message || 'Unknown validation error'),
        severity: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveClick = async () => {
    if (isCaregiver) return;
    // Validate entire form before showing confirmation
    const valid = await trigger();
    if (!valid) {
      setSnackbar({ open: true, message: 'Please check for missing required fields.', severity: 'error' });
      return;
    }
    await handleSubmit(onFinalSubmit)();
  };

  const stepFieldMap: Record<number, (keyof FormData)[]> = {
    0: [
      'dateOfExam',
      'tbTestGivenDate',
      'tbTestReadDate',
      'tbTestType',
      'tbTestResult',
      'tbResultsMm',
      'tbActionTaken',
      'tbStatus',
    ],
    1: [
      'ambulatoryStatus',
      'ambulatoryStatusBasedOn',
      'bedriddenIllness',
      'bedriddenRecoveryFromSurgery',
      'bedriddenOther',
      'bedriddenDurationDays',
      'bedriddenEstimatedDate',
      'bedriddenPermanentExplain',
    ],
    2: [
      'primaryDiagnosis',
      'primaryCanManage',
      'secondaryDiagnosis',
      'secondaryCanManage',
      'contagiousTreatment',
      'contagiousCanManage',
      'allergiesTreatment',
      'allergiesCanManage',
      'otherConditionsTreatment',
      'otherConditionsCanManage',
    ],
    3: [
      'auditoryImpairment',
      'visualImpairment',
      'wearsDentures',
      'wearsProsthesis',
      'specialDiet',
      'substanceAbuseProblem',
      'bowelImpairment',
      'bladderImpairment',
      'motorImpairment',
      'requiresContinuousBedCare',
      'historyOfSkinCondition',
    ],
    4: [
      'mentalConfused',
      'mentalInappropriate',
      'mentalAggressive',
      'mentalWandering',
      'mentalSundowning',
      'mentalAbleFollowInstructions',
      'mentalDepressed',
      'mentalSuicidal',
      'mentalAbleCommunicateNeeds',
      'mentalAtRiskGrooming',
      'mentalAbleLeaveUnassisted',
    ],
    5: [
      'capBath',
      'capDressGroom',
      'capFeed',
      'capToileting',
      'capManageCash',
      'medAdminPrescription',
      'medAdminInjections',
      'medGlucoseTesting',
      'medAdminPRN',
      'medAdminOxygen',
      'medStoreMedications',
    ],
    6: [
      'receivingHospiceCare',
      'hospiceTerminalIllness',
      'physicalHealthStatus',
      'comments',
      'physicianNameAddress',
      'physicianTelephone',
      'physicianLengthOfTimePatient',
      // 'physicianSignature',
      'physicianDate',
    ],
  };

  const handleNext = async () => {
    const isReadOnly = (diagnosis && (diagnosis.status === 'COMPLETED' || diagnosis.status === 'REVIEWED')) || isCaregiver;

    let valid = true;
    const fields = stepFieldMap[activeStep];

    // Only validate if NOT read-only (editing mode)
    if (!isReadOnly) {
      if (fields && fields.length) {
        valid = await trigger(fields as any);
      }
    }

    if (valid) {
      // Move to next tab without saving to backend (all saving happens on final submit)
      setActiveStep((s) => s + 1);
    } else {
      // Extract specific error messages to help user
      const currentErrors = fields ? fields.map((f: any) => (errors as any)[f]?.message).filter(Boolean) : [];
      const errorDetails = currentErrors.length > 0 ? ` (${currentErrors.join(', ')})` : '';

      setSnackbar({
        open: true,
        message: `Please complete the required fields correctly before proceeding.${errorDetails}`,
        severity: 'error',
      });
    }
  };

  const handleBack = () => {
    setActiveStep((s) => s - 1);
  };
  const watchedValues = (useWatch({ control }) || {}) as Partial<FormData>;

  const isExplainEnabled = (key: keyof FormData): boolean => {
    const value = watchedValues?.[key];
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string') {
      return value.trim().toLowerCase() === 'yes';
    }
    return Boolean(value);
  };

  // Handle navigation from Residents page
  React.useEffect(() => {
    const state: any = location.state;

    if (state?.residentId) {
      setSelectedResident(String(state.residentId));
    } else {
      setSelectedResident(null);
    }

    setPatientDetails(state?.patientDetails || null);
  }, [location.state]);

  // Fetch Resident details if missing (for refresh support)
  useEffect(() => {
    if (selectedResident && !patientDetails) {
      residentService.getResidentById(selectedResident)
        .then((res: ResidentDTO) => {
          const bannerData = {
            id: res.id,
            facilityId: res.facilityId,
            name: res.fullName || res.residentName || `${res.firstName || ""} ${res.middleName ? res.middleName + " " : ""}${res.lastName || ""}`.trim() || "N/A",
            EMRNo: res.mrnNumber || res.medicalRecordNumber || res.mrnNumber || res.id || "N/A",
            bp: res.bloodPressure || "N/A",
            weight: res.weight || "N/A",
            height: res.height || "N/A",
            bmi: res.bmi || "N/A",
            room: res.roomNumber && res.bedNumber ? `${res.roomNumber}-${res.bedNumber}` : (res.roomNumber || "Not Assigned"),
            sex: res.gender || "N/A",
            status: res.status || "ACTIVE",
            dob: res.dateOfBirth || undefined,
            age: (res as any).age ?? undefined,
            attendingPhysician: res.attendingPhysicianName || "N/A",
            allergies: res.allergies ? String(res.allergies).split(",").map((a: string) => a.trim()).filter(Boolean) : [],
          };
          setPatientDetails(bannerData);
        })
        .catch(err => console.error("Failed to fetch resident details for diagnosis", err));
    }
  }, [selectedResident, patientDetails]);


  const renderStepContent = (step: number) => {
    if (step === 0) {
      return (
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Controller
                name="dateOfExam"
                control={control}
                render={({ field, fieldState }) => (
                  <DatePicker disabled={isReadOnlyForm}
                    label="Date of Exam"
                    value={field.value}
                    onChange={field.onChange}
                    maxDate={new Date()}
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!fieldState.error,
                        helperText: fieldState.error?.message,
                        InputLabelProps: {
                          sx: { "& .MuiFormLabel-asterisk": { color: "error.main" } }
                        }
                      }
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>

          {/* <Grid item xs={12} sm={4}>
            <Controller
              name="sex"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.sex}>
                  <InputLabel>Sex</InputLabel>
                  <Select disabled={isReadOnlyForm} {...field} label="Sex">
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                  {errors.sex && <FormHelperText>{errors.sex.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="height" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth label="Height" />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="weight" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth label="Weight" />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="bloodPressure" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth label="Blood Pressure" />
            )} />
          </Grid> */}

          {/* TB Test Section */}
          <Grid item xs={12}>
            <Typography variant="h6">Tuberculosis (TB) Test</Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Controller
                name="tbTestGivenDate"
                control={control}
                render={({ field }) => (
                  <DatePicker disabled={isReadOnlyForm}
                    label="Date of TB Test Given"
                    value={field.value}
                    onChange={field.onChange}
                    maxDate={new Date()}
                    format="dd/MM/yyyy"
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>


          <Grid item xs={12} sm={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Controller
                name="tbTestReadDate"
                control={control}
                render={({ field }) => (
                  <DatePicker disabled={isReadOnlyForm}
                    label="Date of TB Test Read"
                    value={field.value}
                    onChange={field.onChange}
                    maxDate={new Date()}
                    format="dd/MM/yyyy"
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>


          <Grid item xs={12} sm={4}>
            <Controller name="tbTestType" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth label="Type of TB Test" InputLabelProps={{ shrink: true }} />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="tbTestResult" control={control} render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>TB Test Result</InputLabel>
                <Select disabled={isReadOnlyForm} {...field} label="TB Test Result">
                  {tbResultOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.valueName.trim()}>
                      {opt.valueName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="tbResultsMm" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth label="Results (mm)" InputLabelProps={{ shrink: true }} />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="tbActionTaken" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth label="Action Taken (if positive)" InputLabelProps={{ shrink: true }} />
            )} />
          </Grid>



          <Grid item xs={12}>
            <Controller name="tbStatus" control={control} render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>TB Status</InputLabel>
                <Select disabled={isReadOnlyForm} {...field} label="TB Status">
                  {tbStatusOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.valueName.trim()}>
                      {opt.valueName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )} />
          </Grid>
        </Grid>
      );
    }

    // Step 1 - Ambulatory
    if (step === 1) {
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6">Ambulatory Status</Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller
              name="ambulatoryStatus"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl fullWidth error={!!fieldState.error}>
                  <InputLabel>Ambulatory Status</InputLabel>
                  <Select disabled={isReadOnlyForm} {...field} label="Ambulatory Status">
                    {ambulatoryStatusOptions.map((opt) => (
                      <MenuItem key={opt.id} value={opt.valueName}>
                        {opt.valueName}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldState.error && (
                    <FormHelperText sx={{ color: "red" }}>
                      {fieldState.error.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={12} sm={8}>
            <Controller
              name="ambulatoryStatusBasedOn"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl fullWidth error={!!fieldState.error}>
                  <InputLabel id="ambulatoryStatusBasedOn-label">
                    If nonambulatory/bedridden, status based upon
                  </InputLabel>
                  <Select disabled={isReadOnlyForm}
                    {...field}
                    labelId="ambulatoryStatusBasedOn-label"
                    label="If nonambulatory/bedridden, status based upon"
                  >
                    {nonambulatoryOptions.map((opt) => (
                      <MenuItem key={opt.id} value={opt.valueName}>
                        {opt.valueName}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldState.error && (
                    <FormHelperText sx={{ color: "red" }}>
                      {fieldState.error.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>


          <Grid item xs={12}>
            <Typography variant="subtitle2">If bedridden, check one or more of the following and describe the nature of the illness, surgery or otherwise</Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="bedriddenIllness" control={control} render={({ field, fieldState }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth label="Illness" InputLabelProps={{ shrink: !!field.value }} error={!!fieldState.error} helperText={fieldState.error?.message} />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="bedriddenRecoveryFromSurgery" control={control} render={({ field, fieldState }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth label="Recovery from Surgery" InputLabelProps={{ shrink: !!field.value }} error={!!fieldState.error} helperText={fieldState.error?.message} />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="bedriddenOther" control={control} render={({ field, fieldState }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth label="Other" InputLabelProps={{ shrink: !!field.value }} error={!!fieldState.error} helperText={fieldState.error?.message} />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="bedriddenDurationDays" control={control} render={({ field, fieldState }) => (
              <TextField disabled={isReadOnlyForm}
                {...field}
                fullWidth
                label="If bedridden, how long (number of days)"
                type="number"
                InputLabelProps={{ shrink: true }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Controller
                name="bedriddenEstimatedDate"
                control={control}
                render={({ field, fieldState }) => (
                  <DatePicker disabled={isReadOnlyForm}
                    label="Estimated date illness or recovery is expected to end"
                    value={field.value}
                    onChange={field.onChange}
                    disablePast
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!fieldState.error,
                        helperText: fieldState.error?.message,
                      },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>


          <Grid item xs={12} sm={4}>
            <Controller name="bedriddenPermanentExplain" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth label="If illness or recovery is permanent, please explain" />
            )} />
          </Grid>
        </Grid>
      );
    }

    // Step 2 - Diagnosis & Treatments (primary, secondary, contagious, allergies, other)
    if (step === 2) {
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6">Primary Diagnosis</Typography>
          </Grid>

          <Grid item xs={12}>
            <Controller name="primaryDiagnosis" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm}
                {...field}
                fullWidth
                label="Primary Diagnosis (treatment/medication/type)"
                error={!!errors.primaryDiagnosis}
                helperText={errors.primaryDiagnosis?.message}
              />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="primaryCanManage" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm} select fullWidth label="Can manage own treatment?" {...field}>
                {treatmentStatusOptions.map(opt => (
                  <MenuItem key={opt.id} value={opt.valueName}>{opt.valueName}</MenuItem>
                ))}
              </TextField>
            )} />
          </Grid>

          <Grid item xs={12} sm={8}>
            <Controller name="primarySupervisionNeeded" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth label="If not, type of medical supervision needed" InputLabelProps={{ shrink: !!field.value }} />
            )} />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6">Secondary Diagnosis(es)</Typography>
          </Grid>

          <Grid item xs={12}>
            <Controller name="secondaryDiagnosis" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth label="Secondary Diagnosis (treatment/medication)" InputLabelProps={{ shrink: !!field.value }} />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="secondaryCanManage" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm} select fullWidth label="Can manage own treatment?" {...field}>
                {treatmentStatusOptions.map(opt => (
                  <MenuItem key={opt.id} value={opt.valueName}>{opt.valueName}</MenuItem>
                ))}
              </TextField>
            )} />
          </Grid>

          <Grid item xs={12} sm={8}>
            <Controller name="secondarySupervisionNeeded" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth label="If not, type of medical supervision needed" InputLabelProps={{ shrink: !!field.value }} />
            )} />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1">Check if applicable:</Typography>
            <Controller name="mildCognitiveImpairment" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox disabled={isReadOnlyForm} checked={!!field.value} onChange={(e: any) => field.onChange(e.target.checked)} />} label="Mild Cognitive Impairment" />
            )} />
            <Controller name="dementia" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox disabled={isReadOnlyForm} checked={!!field.value} onChange={(e: any) => field.onChange(e.target.checked)} />} label="Dementia" />
            )} />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6">Contagious / Infectious Disease</Typography>
          </Grid>

          <Grid item xs={12}>
            <Controller name="contagiousTreatment" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth label="Treatment/medication (type and dosage/equipment)" />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="contagiousCanManage" control={control} render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Can manage own treatment?</InputLabel>
                <Select disabled={isReadOnlyForm} {...field} label="Can manage own treatment?">
                  {treatmentStatusOptions.map(opt => (
                    <MenuItem key={opt.id} value={opt.valueName}>{opt.valueName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )} />
          </Grid>

          <Grid item xs={12} sm={8}>
            <Controller name="contagiousSupervisionNeeded" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth label="If not, type of medical supervision needed" />
            )} />
          </Grid>

          {/* Allergies & Other Conditions */}
          <Grid item xs={12}>
            <Typography variant="h6">Allergies</Typography>
          </Grid>
          <Grid item xs={12}>
            <Controller name="allergiesTreatment" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth label="Treatment/medication (type and dosage/equipment)" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="allergiesCanManage" control={control} render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Can manage own treatment?</InputLabel>
                <Select disabled={isReadOnlyForm} {...field} label="Can manage own treatment?">
                  {treatmentStatusOptions.map(opt => (
                    <MenuItem key={opt.id} value={opt.valueName}>{opt.valueName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <Controller name="allergiesSupervisionNeeded" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth label="If not, type of medical supervision needed" />
            )} />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6">Other Conditions</Typography>
          </Grid>
          <Grid item xs={12}>
            <Controller name="otherConditionsTreatment" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth label="Treatment/medication (type and dosage/equipment)" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="otherConditionsCanManage" control={control} render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Can manage own treatment?</InputLabel>
                <Select disabled={isReadOnlyForm} {...field} label="Can manage own treatment?">
                  {treatmentStatusOptions.map(opt => (
                    <MenuItem key={opt.id} value={opt.valueName}>{opt.valueName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <Controller name="otherConditionsSupervisionNeeded" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth label="If not, type of medical supervision needed" />
            )} />
          </Grid>
        </Grid>
      );
    }

    // Step 3 - Physical Health
    if (step === 3) {
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6">Physical Health Status</Typography>
          </Grid>

          {/** Auditory */}
          <Grid item xs={12} sm={4}>
            <Controller name="auditoryImpairment" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox disabled={isReadOnlyForm} checked={!!field.value} onChange={(e: any) => field.onChange(e.target.checked)} />} label="Auditory Impairment" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="auditoryAssistiveDevice" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth label="Assistive Device (if applicable)" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="auditoryExplain" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Explain" disabled={isReadOnlyForm || !isExplainEnabled('auditoryImpairment')} />
            )} />
          </Grid>

          {/** Visual */}
          <Grid item xs={12} sm={4}>
            <Controller name="visualImpairment" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox disabled={isReadOnlyForm} checked={!!field.value} onChange={(e: any) => field.onChange(e.target.checked)} />} label="Visual Impairment" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="visualAssistiveDevice" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth label="Assistive Device (if applicable)" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="visualExplain" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Explain" disabled={isReadOnlyForm || !isExplainEnabled('visualImpairment')} />
            )} />
          </Grid>

          {/** Dentures / Prosthesis */}
          <Grid item xs={12} sm={4}>
            <Controller name="wearsDentures" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox disabled={isReadOnlyForm} checked={!!field.value} onChange={(e: any) => field.onChange(e.target.checked)} />} label="Wears Dentures" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="wearsDenturesAssistiveDevice" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth label="Assistive Device" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="wearsDenturesExplain" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Explain" disabled={isReadOnlyForm || !isExplainEnabled('wearsDentures')} />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="wearsProsthesis" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox disabled={isReadOnlyForm} checked={!!field.value} onChange={(e: any) => field.onChange(e.target.checked)} />} label="Wears Prosthesis" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="wearsProsthesisAssistiveDevice" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth label="Assistive Device" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="wearsProsthesisExplain" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Explain" disabled={isReadOnlyForm || !isExplainEnabled('wearsProsthesis')} />
            )} />
          </Grid>

          {/** Other items */}
          <Grid item xs={12} sm={4}>
            <Controller name="specialDiet" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox disabled={isReadOnlyForm} checked={!!field.value} onChange={(e: any) => field.onChange(e.target.checked)} />} label="Special Diet" />
            )} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <Controller name="specialDietExplain" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Explain (if applicable)" disabled={isReadOnlyForm || !isExplainEnabled('specialDiet')} />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="substanceAbuseProblem" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox disabled={isReadOnlyForm} checked={!!field.value} onChange={(e: any) => field.onChange(e.target.checked)} />} label="Substance Abuse Problem" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="useOfAlcohol" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox disabled={isReadOnlyForm} checked={!!field.value} onChange={(e: any) => field.onChange(e.target.checked)} />} label="Use of Alcohol" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="useOfCigarettes" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox disabled={isReadOnlyForm} checked={!!field.value} onChange={(e: any) => field.onChange(e.target.checked)} />} label="Use of Cigarettes" />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="bowelImpairment" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox disabled={isReadOnlyForm} checked={!!field.value} onChange={(e: any) => field.onChange(e.target.checked)} />} label="Bowel Impairment" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="bladderImpairment" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox disabled={isReadOnlyForm} checked={!!field.value} onChange={(e: any) => field.onChange(e.target.checked)} />} label="Bladder Impairment" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="motorImpairment" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox disabled={isReadOnlyForm} checked={!!field.value} onChange={(e: any) => field.onChange(e.target.checked)} />} label="Motor Impairment/Paralysis" />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="requiresContinuousBedCare" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox disabled={isReadOnlyForm} checked={!!field.value} onChange={(e: any) => field.onChange(e.target.checked)} />} label="Requires Continuous Bed Care" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="historyOfSkinCondition" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox disabled={isReadOnlyForm} checked={!!field.value} onChange={(e: any) => field.onChange(e.target.checked)} />} label="History of Skin Condition or Breakdown" />
            )} />
          </Grid>
        </Grid>
      );
    }

    // Step 4 - Mental Condition
    if (step === 4) {
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6">Mental Condition</Typography>
          </Grid>

          {[
            { key: 'mentalConfused', label: 'Confused/Disoriented' },
            { key: 'mentalInappropriate', label: 'Inappropriate Behavior' },
            { key: 'mentalAggressive', label: 'Aggressive Behavior' },
            { key: 'mentalWandering', label: 'Wandering Behavior' },
            { key: 'mentalSundowning', label: 'Sundowning Behavior' },
            { key: 'mentalAbleFollowInstructions', label: 'Able to Follow Instructions' },
            { key: 'mentalDepressed', label: 'Depressed' },
            { key: 'mentalSuicidal', label: 'Suicidal/Self-Abuse' },
            { key: 'mentalAbleCommunicateNeeds', label: 'Able to Communicate Needs' },
            { key: 'mentalAtRiskGrooming', label: 'At Risk if Allowed Direct Access to Personal Grooming/Hygiene Items' },
            { key: 'mentalAbleLeaveUnassisted', label: 'Able to Leave Facility Unassisted' },
          ].map((item) => (
            <React.Fragment key={item.key}>

              {/* Checkbox */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <Controller
                    name={item.key as any}
                    control={control}
                    render={({ field }) => {
                      const checked = typeof field.value === 'boolean' ? field.value : String(field.value).toLowerCase() === 'yes';
                      return (
                        <FormControlLabel
                          control={
                            <Checkbox disabled={isReadOnlyForm}
                              checked={checked}
                              onChange={(e: any) => field.onChange(e.target.checked ? 'Yes' : 'No')}
                            />
                          }
                          label={item.label}
                        />
                      );
                    }}
                  />
                </FormControl>
              </Grid>

              {/* Explain field */}
              <Grid item xs={12} sm={8}>
                <Controller
                  name={`${item.key}Explain` as any}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Explain"
                      disabled={isReadOnlyForm || !isExplainEnabled(item.key as keyof FormData)} // <-- editable only if checkbox is checked
                    />
                  )}
                />
              </Grid>

            </React.Fragment>
          ))}
        </Grid>
      );
    }




    // Step 5 - Capacity & Medication
    if (step === 5) {
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6">Capacity for Self-Care</Typography>
          </Grid>
          {[
            { key: 'capBath', label: 'Able to Bathe Self' },
            { key: 'capDressGroom', label: 'Able to Dress/Groom Self' },
            { key: 'capFeed', label: 'Able to Feed Self' },
            { key: 'capToileting', label: 'Able to Care for Own Toileting Needs' },
            { key: 'capManageCash', label: 'Able to Manage Own Cash Resources' },
          ].map((item) => (
            <React.Fragment key={item.key}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <Controller name={item.key as any} control={control} render={({ field }) => {
                    const checked = typeof field.value === 'boolean' ? field.value : String(field.value).toLowerCase() === 'yes';
                    return (
                      <FormControlLabel
                        control={(
                          <Checkbox disabled={isReadOnlyForm}
                            checked={checked}
                            onChange={(e: any) => field.onChange(e.target.checked ? 'Yes' : 'No')}
                          />
                        )}
                        label={item.label}
                      />
                    );
                  }} />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Controller name={`${item.key}Explain` as any} control={control} render={({ field }) => (
                  <TextField {...field} fullWidth label="Explain" disabled={isReadOnlyForm || !isExplainEnabled(item.key as keyof FormData)} />
                )} />
              </Grid>
            </React.Fragment>
          ))}

          <Grid item xs={12}>
            <Typography variant="h6">Medication Management</Typography>
          </Grid>
          {[
            { key: 'medAdminPrescription', label: 'Able to Administer Own Prescription Medications' },
            { key: 'medAdminInjections', label: 'Able to Administer Own Injections' },
            { key: 'medGlucoseTesting', label: 'Able to Perform Own Glucose Testing' },
            { key: 'medAdminPRN', label: 'Able to Administer Own PRN Medications' },
            { key: 'medAdminOxygen', label: 'Able to Administer Own Oxygen' },
            { key: 'medStoreMedications', label: 'Able to Store Own Medications' },
          ].map((item) => (
            <React.Fragment key={item.key}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <Controller name={item.key as any} control={control} render={({ field }) => {
                    const checked = typeof field.value === 'boolean' ? field.value : String(field.value).toLowerCase() === 'yes';
                    return (
                      <FormControlLabel
                        control={(
                          <Checkbox disabled={isReadOnlyForm}
                            checked={checked}
                            onChange={(e: any) => field.onChange(e.target.checked ? 'Yes' : 'No')}
                          />
                        )}
                        label={item.label}
                      />
                    );
                  }} />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Controller name={`${item.key}Explain` as any} control={control} render={({ field }) => (
                  <TextField {...field} fullWidth label="Explain" disabled={isReadOnlyForm || !isExplainEnabled(item.key as keyof FormData)} />
                )} />
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      );
    }

    // Step 6 - Hospice / Physician
    if (step === 6) {
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6">Hospice / Physical Health / Physician Information</Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="receivingHospiceCare" control={control} render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Receiving Hospice Care?</InputLabel>
                <Select disabled={isReadOnlyForm} {...field} label="Receiving Hospice Care?">
                  {treatmentStatusOptions.map(opt => (
                    <MenuItem key={opt.id} value={opt.valueName}>{opt.valueName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <Controller name="hospiceTerminalIllness" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth label="If yes, specify the terminal illness" />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="physicalHealthStatus" control={control} render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Physical Health Status</InputLabel>
                <Select disabled={isReadOnlyForm} {...field} label="Physical Health Status">
                  <MenuItem value="">
                    <em>Select status</em>
                  </MenuItem>
                  {physicalHealthStatusOptions.map(opt => (
                    <MenuItem key={opt.id} value={opt.valueName}>{opt.valueName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )} />
          </Grid>

          <Grid item xs={12}>
            <Controller name="comments" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth multiline rows={4} label="Comments" />
            )} />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1">Physician Information</Typography>
          </Grid>

          <Grid item xs={12}>
            <Controller name="physicianNameAddress" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth label="Physician Name and Address (print)" />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller
              name="physicianTelephone"
              control={control}
              rules={{
                required: "Telephone is required",
                pattern: {
                  value: /^[0-9]{0,10}$/,
                  message: "Only numbers allowed, max 10 digits"
                }
              }}
              render={({ field }) => (
                <TextField disabled={isReadOnlyForm}
                  {...field}
                  fullWidth
                  label="Telephone"
                  inputProps={{
                    maxLength: 10,
                    inputMode: "numeric",
                    pattern: "[0-9]*"
                  }}
                  error={!!errors.physicianTelephone}
                  helperText={errors.physicianTelephone?.message}
                  onChange={(e: any) => {
                    const onlyNums = e.target.value.replace(/\D/g, "");
                    field.onChange(onlyNums.slice(0, 10));
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="physicianLengthOfTimePatient" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm} {...field} fullWidth label="Length of time resident has been your patient" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Controller
                name="physicianDate"
                control={control}
                render={({ field }) => (
                  <DatePicker disabled={isReadOnlyForm}
                    label="Date"
                    value={field.value}
                    onChange={field.onChange}
                    maxDate={new Date()}
                    format="dd/MM/yyyy"
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>


          {/* <Grid item xs={12}>
            <Controller name="physicianSignature" control={control} render={({ field }) => (
              <TextField disabled={isReadOnlyForm}
                {...field}
                fullWidth
                label="Physician Signature"
                error={!!errors.physicianSignature}
                helperText={errors.physicianSignature?.message}
              />
            )} />
          </Grid> */}
        </Grid>
      );
    }

    return null;
  };

  const getResident = (id: string | null) =>
    mockResidents.find((r) => String(r.id) === String(id)) || null;
  const calcAge = (dob: string) => {
    try {
      if (!dob) return '';
      const birth = new Date(dob);
      if (isNaN(birth.getTime())) return '';
      const diff = Date.now() - birth.getTime();
      const ageDt = new Date(diff);
      return Math.abs(ageDt.getUTCFullYear() - 1970);
    } catch {
      return '';
    }
  };

  const bannerResidentRaw = (patientDetails as any) || getResident(selectedResident);
  const bannerResident = bannerResidentRaw ? {
    ...bannerResidentRaw,
    name: (diagnosis?.residentName && !diagnosis.residentName.includes("undefined"))
      ? diagnosis.residentName
      : (bannerResidentRaw.name && !bannerResidentRaw.name.includes("undefined")
        ? bannerResidentRaw.name
        : (diagnosis?.residentName || bannerResidentRaw.name || "N/A")),
    EMRNo: diagnosis?.mrnNumber || bannerResidentRaw.EMRNo || "N/A",
    age: diagnosis?.age ?? (bannerResidentRaw as any)?.age,
    bp: latestVitalBp || (diagnosis as any)?.bp || (bannerResidentRaw as any).bp,
    height: (diagnosis as any)?.height ?? (bannerResidentRaw as any).height,
    weight: latestVitalWeight || ((diagnosis as any)?.weight ?? (bannerResidentRaw as any).weight),
    bmi: (diagnosis as any)?.bmi ?? (bannerResidentRaw as any).bmi,
    room: (diagnosis as any)?.room ?? (bannerResidentRaw as any).room,
    sex: (diagnosis as any)?.sex ?? (bannerResidentRaw as any).sex,
    dob: (bannerResidentRaw as any)?.dob || (bannerResidentRaw as any)?.dateOfBirth,
    attendingPhysician: (diagnosis as any)?.attendingPhysician || (bannerResidentRaw as any).attendingPhysician,
    bloodGroup: residentBloodGroup || (bannerResidentRaw as any).bloodGroup || "N/A",
  } : (diagnosis ? {
    id: diagnosis.residentId,
    name: diagnosis.residentName || "N/A",
    EMRNo: diagnosis.mrnNumber || "N/A",
    bp: latestVitalBp || (diagnosis as any).bp || diagnosis.examVitals?.bloodPressure || "N/A",
    weight: latestVitalWeight || (diagnosis as any).weight || diagnosis.examVitals?.weight || "N/A",
    height: (diagnosis as any).height || diagnosis.examVitals?.height || "N/A",
    bmi: (diagnosis as any).bmi ? String((diagnosis as any).bmi) : (diagnosis.examVitals?.bmi ? String(diagnosis.examVitals.bmi) : "N/A"),
    room: diagnosis.room || "Not Assigned",
    sex: diagnosis.sex || "N/A",
    dob: (diagnosis as any).dob || (diagnosis as any).dateOfBirth || "",
    attendingPhysician: diagnosis.attendingPhysician || "N/A",
    bloodGroup: residentBloodGroup || (diagnosis as any).bloodGroup || "N/A",
    allergies: [],
    age: diagnosis.age,
  } : null);

  const onErrors = (fieldErrors: any) => {

    setSnackbar({
      open: true,
      message: 'Please check for missing required fields.',
      severity: 'error',
    });
  };

  return (
    <Box sx={{
      width: "100%",
      maxWidth: "100%",
      px: 0,
      '& .MuiInputLabel-root': {
        color: '#1A2B45 !important',
        '&.Mui-focused': {
          color: '#1A2B45 !important',
        },
      },
      '& .MuiFormControlLabel-label': {
        color: '#1A2B45 !important',
      },
    }}>
      {isCaregiver && (
        <Alert severity="warning" sx={{
          mt: 2,
          mb: 2,
          py: 0,
          px: 2,
          alignItems: 'center',
          borderRadius: 2,
          fontWeight: 500,
          fontSize: '0.85rem',
          bgcolor: '#fff9e6', // lighter yellow
          color: '#856404',
          '& .MuiAlert-icon': {
            color: '#856404'
          }
        }}>
          You have read-only access to this section. Creating or editing diagnosis information is not permitted.
        </Alert>
      )}

      <Box sx={{ mt: 3, mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <PatientDiagnosisSvgIcon size={40} color="#48CAE4" />
        </Box>
        <Box>
          <Typography variant="h5" component="h1" fontWeight="700" sx={{ mb: 0.5, color: '#1A2B45' }}>
            Resident Diagnosis
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.85rem" }}>
            Record and manage comprehensive resident clinical evaluations and diagnosis
          </Typography>
        </Box>
      </Box>

      {/* Loading Indicator */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Resident Diagnosis message */}
      {(error || (snackbar.open && snackbar.message)) && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2, fontWeight: 500 }}>
          {error || snackbar.message}
        </Alert>
      )}

      {/* Saving Indicator */}
      {saving && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<CircularProgress size={20} />}>
          Saving changes...
        </Alert>
      )}


      {/* Patient Details Header */}
      <Card
        sx={{
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          background: '#ffffff',
          boxShadow: '0 4px 10px rgba(0,0,0,0.03)'
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 }, '&:last-child': { pb: { xs: 2, md: 3 } } }}>
          {bannerResident ? (
            <Grid container spacing={3} alignItems="stretch">
              {/* LEFT COLUMN: Avatar & Profile Info */}
              <Grid item xs={12} md={7} sx={{ display: 'flex', gap: 2.5, alignItems: 'center' }}>
                {/* Display-only avatar — fetched from Profile GET API */}
                <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, ml: 2 }}>
                  <ResidentAvatar
                    residentId={selectedResident || undefined}
                    residentName={bannerResident.name}
                    sx={{
                      width: 96,
                      height: 96,
                      fontSize: '2.3rem',
                      bgcolor: 'primary.main',
                      boxShadow: '0 4px 10px rgba(0,0,0,0.05)'
                    }}
                  />
                </Box>

                {/* Profile details text */}
                <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, ml: 6 }}>
                  <Typography variant="h5" fontWeight="900" color="#1e293b" sx={{ fontSize: '1.8rem !important', lineHeight: 1.5, letterSpacing: '-0.3px', wordBreak: 'break-all' }}>
                    {bannerResident.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.5, mb: 1.5 }}>
                    MRN: <Box component="span" fontWeight="700" color="text.primary">{bannerResident.EMRNo || bannerResident.mrnNumber || 'N/A'}</Box>
                  </Typography>
                  {/* Demographics chips */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', px: 1.5, py: 0.5, fontSize: '0.8125rem', color: '#475569', fontWeight: '600' }}>
                      Age: {bannerResident.dob ? (calcAge(bannerResident.dob) || 'N/A') : (bannerResident.age !== undefined && bannerResident.age !== null ? bannerResident.age : 'N/A')}
                    </Box>
                    <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', px: 1.5, py: 0.5, fontSize: '0.8125rem', color: '#475569', fontWeight: '600' }}>
                      Gender: {bannerResident.sex || 'N/A'}
                    </Box>
                    <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', px: 1.5, py: 0.5, fontSize: '0.8125rem', color: '#475569', fontWeight: '600' }}>
                      Room: {bannerResident.room || 'N/A'}
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* VERTICAL DIVIDER */}
              <Grid item xs="auto" sx={{ display: { xs: 'none', md: 'block' } }}>
                <Divider orientation="vertical" sx={{ height: '100%', borderColor: '#e2e8f0' }} />
              </Grid>

              {/* RIGHT COLUMN: Physician & Allergies */}
              <Grid item xs={12} md>
                <Box
                  sx={{
                    bgcolor: '#f8fafc',
                    borderRadius: 2,
                    p: { xs: 1.5, md: 2 },
                    border: '1px solid #f1f5f9',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    gap: 2
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight="600" display="block" sx={{ mb: 0.25 }}>
                      Attending Physician
                    </Typography>
                    <Typography variant="body2" color="text.primary" fontWeight="700" sx={{ wordBreak: "break-all" }}>
                      {bannerResident.attendingPhysician || "Unassigned"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight="600" display="block" sx={{ mb: 0.5 }}>
                      Known Allergies
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {!bannerResident.allergies || bannerResident.allergies.length === 0 ? (
                        <Typography variant="body2" color="text.primary" fontWeight="700">
                          None Known
                        </Typography>
                      ) : (
                        bannerResident.allergies.map((a: string) => (
                          <Chip
                            key={a}
                            label={a}
                            size="small"
                            sx={{
                              fontWeight: "600",
                              background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                              color: "#fff",
                              height: 24,
                              "& .MuiChip-label": { px: 1 }
                            }}
                          />
                        ))
                      )}
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          ) : (
            <Box py={3} textAlign="center">
              <Typography variant="body1" color="text.secondary">
                No resident selected. Open this page from a resident to load their details.
              </Typography>
            </Box>
          )}
        </CardContent>

        {/* VITALS PANEL ROW */}
        {bannerResident && (
          <>
            <Divider sx={{ borderColor: '#e2e8f0' }} />
            <Box
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-around',
                flexWrap: 'wrap',
                gap: { xs: 2, md: 4 },
                background: '#fafafa',
                borderBottomLeftRadius: 12,
                borderBottomRightRadius: 12
              }}
            >
              {/* BP */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: '#f1f5f9', display: 'flex', color: '#64748b' }}>
                  <BloodPressureSvgIcon sx={{ color: '#64748b', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="600" display="block" sx={{ lineHeight: 1 }}>
                    Blood Pressure
                  </Typography>
                  <Typography variant="body2" color="text.primary" fontWeight="700">
                    {bannerResident.bp || "N/A"}
                  </Typography>
                </Box>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ borderColor: '#e2e8f0' }} />

              {/* Height */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: '#f1f5f9', display: 'flex', color: '#64748b' }}>
                  <StraightenIcon sx={{ color: '#64748b', fontSize: 24, transform: 'rotate(90deg)' }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="600" display="block" sx={{ lineHeight: 1 }}>
                    Height
                  </Typography>
                  <Typography variant="body2" color="text.primary" fontWeight="700">
                    {bannerResident.height && bannerResident.height !== "N/A"
                      ? (/inch|in|cm|ft|'/i.test(String(bannerResident.height))
                        ? bannerResident.height
                        : `${bannerResident.height} inch`)
                      : "N/A"}
                  </Typography>
                </Box>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ borderColor: '#e2e8f0' }} />

              {/* Weight */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: '#f1f5f9', display: 'flex', color: '#64748b' }}>
                  <WeightSvgIcon sx={{ color: '#64748b', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="600" display="block" sx={{ lineHeight: 1 }}>
                    Weight
                  </Typography>
                  <Typography variant="body2" color="text.primary" fontWeight="700">
                    {bannerResident.weight && bannerResident.weight !== "N/A"
                      ? (/lbs|lb|kg/i.test(String(bannerResident.weight))
                        ? bannerResident.weight
                        : `${bannerResident.weight} lbs`)
                      : "N/A"}
                  </Typography>
                </Box>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ borderColor: '#e2e8f0' }} />

              {/* BMI */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: '#f1f5f9', display: 'flex', color: '#64748b' }}>
                  <BmiSvgIcon sx={{ color: '#64748b', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="600" display="block" sx={{ lineHeight: 1 }}>
                    BMI
                  </Typography>
                  <Typography variant="body2" color="text.primary" fontWeight="700">
                    {bannerResident.bmi || "N/A"}
                  </Typography>
                </Box>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ borderColor: '#e2e8f0' }} />

              {/* Blood Group */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: '#f1f5f9', display: 'flex', color: '#64748b' }}>
                  <BloodGroupSvgIcon sx={{ color: '#64748b', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="600" display="block" sx={{ lineHeight: 1 }}>
                    Blood Group
                  </Typography>
                  <Typography variant="body2" color="text.primary" fontWeight="700">
                    {bannerResident.bloodGroup || "N/A"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Card>

      {success && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Alert severity="success">Patient diagnosis saved successfully.</Alert>
        </Paper>
      )}

      <Paper sx={{ p: 3 }}>
        <Stepper
          activeStep={activeStep}
          orientation={isMobile ? 'vertical' : 'horizontal'}
          sx={{
            mb: 3,
            "& .MuiStepIcon-root.Mui-active": { color: "#48CAE4" },
            "& .MuiStepIcon-root.Mui-completed": { color: "#48CAE4" },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <form onSubmit={handleSubmit(onFinalSubmit, onErrors)}>
          <fieldset disabled={isReadOnlyForm} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
            {steps.map((_, index) => (
              <Box key={index} sx={{ display: activeStep === index ? 'block' : 'none' }}>
                {renderStepContent(index)}
              </Box>
            ))}
          </fieldset>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Box>
              {activeStep > 0 && (
                <Button onClick={handleBack} sx={{ mr: 1 }}>Back</Button>
              )}
            </Box>

            <Box>
              {/* <Button variant="outlined" onClick={() => { reset(); setSuccess(false); }} sx={{ mr: 2 }}>Reset</Button> */}
              {activeStep < steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={Boolean(saving)}
                  sx={{ bgcolor: "#48CAE4", color: "#000000", fontWeight: "normal", "&:hover": { bgcolor: "#3ab3cc" } }}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleSaveClick}
                  disabled={saveDisabled}
                  sx={{ bgcolor: "#48CAE4", color: "#000000", fontWeight: "normal", "&:hover": { bgcolor: "#3ab3cc" } }}
                >
                  {diagnosis?.id ? "Update Diagnosis" : "Save Diagnosis"}
                </Button>
              )}
            </Box>

          </Box>
        </form>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default PatientDiagnosisPage;